/**
 * eventRisk.js — Predictive event risk scoring engine.
 *
 * Scores upcoming events by operational risk BEFORE they become problems,
 * using time-to-event proximity and existing attention/pipeline signals.
 *
 * No backend calls. No mutations. Pure client-side derivation.
 *
 * RISK MODEL:
 *   {
 *     level:        'low' | 'medium' | 'high'
 *     score:        number (0–100+)
 *     daysUntil:    number | null
 *     reasons:      string[]
 *     missingAreas: string[]
 *     urgency:      'future' | 'approaching' | 'urgent' | 'same_day' | 'past'
 *   }
 */

import { computeEventAttentionLight } from './eventAttention';
import { parseLocalDate } from '../utils/dateFormatters';
import { tenantTodayMidnight } from './datetime';

/* ── Time helpers ── */

export const getDaysUntilEvent = (eventDate) => {
  const d = parseLocalDate(eventDate);
  if (!d) return null;
  // ROOT CAUSE 2026-08-18: "today" was the VIEWER's browser midnight, not the
  // tenant's. urgency 'past' makes autoGuide.js and riskInsights.js DROP the
  // event entirely, so a viewer one zone away lost day-of guidance.
  // Second defect: the old Math.ceil over a local-midnight delta returned
  // days+1 for any span crossing the November DST fall-back (25h day).
  // Date.UTC has no DST, so calendar-day subtraction is exact.
  const now = tenantTodayMidnight();
  const target = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
};

function getUrgency(daysUntil) {
  if (daysUntil === null) return 'future';
  if (daysUntil < 0) return 'past';
  if (daysUntil === 0) return 'same_day';
  if (daysUntil <= 3) return 'urgent';
  if (daysUntil <= 7) return 'approaching';
  return 'future';
}

function timeScore(daysUntil) {
  if (daysUntil === null) return 0;
  if (daysUntil <= 0) return 40;
  if (daysUntil === 1) return 35;
  if (daysUntil <= 3) return 25;
  if (daysUntil <= 7) return 15;
  if (daysUntil <= 14) return 8;
  return 0;
}

/* ── Pipeline stage → operational area gaps ── */

const STAGE_GAPS = {
  new_lead:       ['proposal', 'contract', 'staffing', 'logistics', 'planning'],
  new_inquiry:    ['proposal', 'contract', 'staffing', 'logistics', 'planning'],
  call_requested: ['proposal', 'contract', 'staffing', 'logistics', 'planning'],
  intake_complete:     ['proposal', 'contract', 'staffing', 'logistics'],
  under_review:        ['proposal', 'contract', 'staffing', 'logistics'],
  reviewing:           ['proposal', 'contract', 'staffing', 'logistics'],
  contacted:           ['proposal', 'contract', 'staffing', 'logistics'],
  consult_scheduled:   ['proposal', 'contract', 'staffing', 'logistics'],
  proposal_ready:      ['contract', 'staffing', 'logistics'],
  proposal_sent:       ['contract', 'staffing', 'logistics'],
  proposal_revision_requested: ['proposal', 'contract', 'staffing', 'logistics'],
  proposal_approved:   ['contract', 'staffing', 'logistics'],
  contract_ready:      ['staffing', 'logistics'],
  contract_sent:       ['staffing', 'logistics'],
  awaiting_signature:  ['staffing', 'logistics'],
  booked:              ['staffing', 'logistics', 'planning'],
  planning_active:     ['logistics'],
  ready_for_execution: [],
};

const AREA_REASONS = {
  staffing:  'Staffing is not fully confirmed',
  logistics: 'Timeline details still need review',
  planning:  'Planning details are incomplete',
  contract:  'Contract is not finalized',
  proposal:  'Proposal has not been completed',
};

/* ── Simple event completeness checks ── */

function checkSimpleEventCompleteness(event, doneAreas = new Set()) {
  const reasons = [];
  const missingAreas = [];
  let score = 0;

  // Staff completeness — skipped when readiness says staffing is done.
  const staffAssigned = event.assignedStaff?.length || 0;
  const staffRequired = event.staffRequired || 0;
  if (!doneAreas.has('staffing') && staffRequired > 0 && staffAssigned < staffRequired) {
    const severity = staffAssigned === 0 ? 15 : 10;
    score += severity;
    missingAreas.push('staffing');
    reasons.push(`Only ${staffAssigned} of ${staffRequired} required staff assigned`);
  }

  // Pending staff confirmations
  if (!doneAreas.has('staffing') && Array.isArray(event.assignedStaff) && event.assignedStaff.length > 0) {
    const pendingCount = event.assignedStaff.filter(s =>
      typeof s === 'object' && s.status === 'pending'
    ).length;
    if (pendingCount > 0) {
      score += 8;
      if (!missingAreas.includes('staffing')) missingAreas.push('staffing');
      reasons.push(`${pendingCount} staff pending confirmation`);
    }
  }

  // Location
  if (!doneAreas.has('logistics') && !event.location && !event.locationId) {
    score += 12;
    missingAreas.push('logistics');
    reasons.push('No venue or location set');
  }

  // Run of show / timeline. Reported under 'timeline', not 'logistics' —
  // filing it under logistics meant skipping the Timeline module could never
  // clear it, because every consumer downstream saw a logistics problem.
  if (!doneAreas.has('logistics') && !doneAreas.has('timeline') && (!event.runOfShow || event.runOfShow.length === 0)) {
    score += 8;
    if (!missingAreas.includes('timeline')) missingAreas.push('timeline');
    reasons.push('No run of show or timeline set');
  }

  // Attire
  if (!doneAreas.has('staffing') && !event.attire) {
    score += 5;
    reasons.push('Staff attire not specified');
  }

  // Parking / load-in notes
  if (!doneAreas.has('logistics') && !event.parkingNotes) {
    score += 3;
    reasons.push('Parking / load-in notes missing');
  }

  // Planning form status (dynamic planning engine)
  if (!doneAreas.has('planning') && event.planningFormStatus === 'not_started') {
    score += 12;
    if (!missingAreas.includes('planning')) missingAreas.push('planning');
    reasons.push('Client planning form has not been started');
  } else if (!doneAreas.has('planning') && event.planningFormStatus === 'in_progress') {
    score += 6;
    if (!missingAreas.includes('planning')) missingAreas.push('planning');
    reasons.push('Client planning form is still in progress');
  }

  return { score, reasons, missingAreas };
}

/**
 * Areas the canonical readiness service marks DONE (complete or skipped) for
 * this event — so the risk engine never re-flags a finished or intentionally
 * skipped module. Reads from options.readiness.modules (preferred) or, as a
 * convenience, event.skippedModules when present. Empty set when neither is
 * available, so behavior is unchanged for callers that pass no readiness.
 */
function resolveDoneAreas(event, options) {
  const done = new Set();
  const mods = options?.readiness?.modules;
  if (Array.isArray(mods)) {
    for (const m of mods) {
      if (m && (m.skipped || ['complete', 'skipped', 'not_applicable'].includes(m.status))) {
        done.add(m.moduleKey);
      }
    }
  }
  // Fallback: honor a skippedModules object riding on the event itself.
  const sm = event?.skippedModules;
  if (sm && typeof sm === 'object') {
    for (const [k, v] of Object.entries(sm)) {
      if (v && (v.skipped === true || v.mode === 'skipped' || v.mode === 'complete')) {
        done.add(k);
      }
    }
  }
  return done;
}

/* ── Core scoring ── */

/**
 * Compute predictive risk for a single event.
 * Reuses computeEventAttentionLight for pipeline-aware signals.
 */
export const computeEventRisk = (event, options = {}) => {
  if (!event) return safeRisk();

  const daysUntil = getDaysUntilEvent(event.eventDate || event.date);
  const urgency = getUrgency(daysUntil);

  // Past events → low risk
  if (urgency === 'past' && !options.includePast) return safeRisk(daysUntil, 'past');

  let score = 0;
  const reasons = [];
  const missingAreas = [];

  // Areas the canonical readiness marks done (complete/skipped) — never
  // re-flag them. Empty when no readiness is supplied (behavior unchanged).
  const doneAreas = resolveDoneAreas(event, options);

  // A) Time urgency
  const tScore = timeScore(daysUntil);
  score += tScore;
  if (daysUntil !== null && daysUntil <= 3 && daysUntil >= 0) {
    reasons.push('This event is approaching soon');
  }

  // B) Pipeline-based missing areas (for pipeline events). Areas the readiness
  // service says are done are filtered out BEFORE scoring, so a booked event
  // whose staffing/logistics/planning are complete or skipped is not treated
  // as if those areas were still open.
  const stageKey = event.effectiveStage || event.pipelineStage || event.leadStatus?.toLowerCase().replace(/\s+/g, '_');
  const stageGaps = STAGE_GAPS[stageKey] || [];
  const gaps = stageGaps.filter(a => !doneAreas.has(a));
  if (gaps.length > 0) {
    gaps.forEach(area => missingAreas.push(area));
    if (gaps.includes('staffing')) { score += 25; reasons.push(AREA_REASONS.staffing); }
    else if (gaps.length >= 3) { score += 20; reasons.push('Multiple planning areas still need attention'); }

    if (gaps.includes('logistics') && !gaps.includes('staffing')) { score += 20; reasons.push(AREA_REASONS.logistics); }
    if (gaps.includes('contract') && daysUntil !== null && daysUntil <= 14) { score += 10; reasons.push(AREA_REASONS.contract); }
    if (gaps.includes('proposal') && daysUntil !== null && daysUntil <= 14) { score += 10; reasons.push(AREA_REASONS.proposal); }
  }

  // B2) Simple event completeness checks (non-pipeline events)
  if (!stageKey || stageGaps.length === 0) {
    const completeness = checkSimpleEventCompleteness(event, doneAreas);
    score += completeness.score;
    completeness.reasons.forEach(r => { if (!reasons.includes(r)) reasons.push(r); });
    completeness.missingAreas.forEach(a => { if (!missingAreas.includes(a)) missingAreas.push(a); });
  }

  // C) Attention level boost (reuse existing engine)
  const att = computeEventAttentionLight(event);
  if (att.level === 'critical') { score += 25; if (!reasons.some(r => r.includes('attention'))) reasons.push('Event has critical attention flags'); }
  else if (att.level === 'warning') { score += 15; if (!reasons.some(r => r.includes('follow-up') || r.includes('review'))) reasons.push('Event has items needing follow-up'); }

  // D) Determine level
  const level = score >= 55 ? 'high' : score >= 25 ? 'medium' : 'low';

  return { level, score, daysUntil, reasons, missingAreas, urgency };
};

/**
 * Lightweight version — returns level + score only.
 */
export const computeEventRiskLight = (event, options = {}) => {
  const risk = computeEventRisk(event, options);
  return { level: risk.level, score: risk.score, daysUntil: risk.daysUntil, urgency: risk.urgency };
};

/**
 * Human-readable label for a risk level.
 */
export const getEventRiskLabel = (level) => {
  const labels = { high: 'High Risk', medium: 'Medium Risk', low: 'Low Risk' };
  return labels[level] || 'Low Risk';
};

/* ── Safe fallback ── */

function safeRisk(daysUntil = null, urgency = 'future') {
  return { level: 'low', score: 0, daysUntil, reasons: [], missingAreas: [], urgency };
}
